/* ──────────────────────────────────────────────────────────────────────
 * JEJU AR Try-On Engine
 * ----------------------------------------------------------------------
 * Real-time facial skincare effect simulator using MediaPipe FaceMesh
 * (468 landmarks) running fully in the browser. No data leaves device.
 *
 * Per-product effect profile is read from <script id="ar-profile-{id}">
 * (emitted by snippets/ar-effect-profile.liquid). Each effect maps to
 * a region-specific filter pass executed on a 2D canvas:
 *
 *   brighten        → +brightness/saturation on skin mask
 *   smooth          → bilateral-style blur on cheeks/forehead/chin
 *   hydrate         → glossy highlight + subtle saturation
 *   redness_reduce  → red-channel attenuation on skin mask
 *   clarify         → smoothing + spot dimming + cool tone
 *   firm            → jaw + cheekbone highlight (mock contour)
 *   sun_glow        → dewy soft glow + cool tone
 *   undereye_lift   → brighten under-eye triangles
 *   lip_tint        → tinted overlay on lip polygon
 *   even_tone       → reduce skin variance (median tone shift)
 *   warm_glow       → warm tone shift (Korean glass-skin look)
 *
 * No external builds; depends only on MediaPipe FaceMesh from jsDelivr.
 * ────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  if (window.__jejuArTryOnInit) return;
  window.__jejuArTryOnInit = true;

  const MEDIAPIPE_FACEMESH_URL =
    'https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/face_mesh.js';

  // MediaPipe FaceMesh landmark groups (subset we use)
  const LM = {
    // Face oval (outer)
    FACE_OVAL: [10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379, 378, 400, 377, 152, 148, 176, 149, 150, 136, 172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109],
    // Cheeks
    LEFT_CHEEK: [50, 101, 36, 205, 187, 207, 216, 212, 138, 215],
    RIGHT_CHEEK: [280, 330, 266, 425, 411, 427, 436, 432, 367, 435],
    // Forehead
    FOREHEAD: [10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 137, 93, 234, 127, 162, 21, 54, 103, 67, 109],
    // Under-eye triangles
    LEFT_UNDEREYE: [33, 7, 163, 144, 145, 153, 154, 155, 133, 173, 157, 158, 159, 160, 161, 246],
    RIGHT_UNDEREYE: [263, 249, 390, 373, 374, 380, 381, 382, 362, 398, 384, 385, 386, 387, 388, 466],
    LEFT_UNDEREYE_LOWER: [33, 130, 25, 110, 24, 23, 22, 26, 112, 243],
    RIGHT_UNDEREYE_LOWER: [263, 359, 255, 339, 254, 253, 252, 256, 341, 463],
    // Lips (outer + inner combined for fill)
    LIPS_OUTER: [61, 146, 91, 181, 84, 17, 314, 405, 321, 375, 291, 409, 270, 269, 267, 0, 37, 39, 40, 185],
    // Jaw line
    JAW: [172, 136, 150, 149, 176, 148, 152, 377, 400, 378, 379, 365, 397],
    // Nose bridge (for highlight)
    NOSE_BRIDGE: [6, 197, 195, 5, 4, 1, 19, 94, 2],
    // Feature regions to exclude from the beauty face mask
    LEFT_EYE:   [33, 246, 161, 160, 159, 158, 157, 173, 133, 155, 154, 153, 145, 144, 163, 7],
    RIGHT_EYE:  [263, 466, 388, 387, 386, 385, 384, 398, 362, 382, 381, 380, 374, 373, 390, 249],
    LEFT_BROW:  [70, 63, 105, 66, 107, 55, 65, 52, 53, 46],
    RIGHT_BROW: [300, 293, 334, 296, 336, 285, 295, 282, 283, 276],
    NOSTRILS:   [219, 235, 75, 98, 2, 326, 327, 305, 455, 439]
  };

  // ── State ────────────────────────────────────────────────────────────
  let faceMesh = null;
  let stream = null;
  let rafId = null;
  let modalEl = null;
  let videoEl = null;
  let canvasEl = null;
  let ctx = null;
  let intensityEl = null;
  let toggleEl = null;
  let toggleLabelEl = null;
  let intensityLabelEl = null;
  let summaryEl = null;
  let loadingEl = null;
  let loadingTextEl = null;
  let errorEl = null;
  let errorTextEl = null;
  let currentProfile = null;
  let currentProductHandle = null;
  let lastLandmarks = null;
  let showEffects = true;
  let intensity = 0; // Default: Day 0 / your skin now (no effect, baseline for comparison)

  // Offscreen buffers for feathered-mask compositing (cached across frames)
  let bufCanvas = null, bufCtx = null;
  let maskCanvas = null, maskCtx = null;
  function ensureBuffers(w, h) {
    if (!bufCanvas) { bufCanvas = document.createElement('canvas'); bufCtx = bufCanvas.getContext('2d'); }
    if (bufCanvas.width !== w || bufCanvas.height !== h) { bufCanvas.width = w; bufCanvas.height = h; }
    if (!maskCanvas) { maskCanvas = document.createElement('canvas'); maskCtx = maskCanvas.getContext('2d'); }
    if (maskCanvas.width !== w || maskCanvas.height !== h) { maskCanvas.width = w; maskCanvas.height = h; }
  }

  /**
   * Draw a blurred copy of the main canvas, masked to a polygon group with a
   * FEATHERED edge so the boundary fades into the surrounding pixels instead
   * of showing a hard seam (the "chin line" problem).
   */
  function drawBlurredRegion(ctx, lm, group, w, h, blurRadius, alpha, featherPx) {
    ensureBuffers(w, h);
    // 1. Blurred source into buffer
    bufCtx.save();
    bufCtx.setTransform(1, 0, 0, 1, 0, 0);
    bufCtx.clearRect(0, 0, w, h);
    bufCtx.filter = `blur(${blurRadius}px)`;
    bufCtx.drawImage(canvasEl, 0, 0, w, h);
    bufCtx.restore();
    // 2. Feathered mask (solid shape blurred for soft edges)
    maskCtx.save();
    maskCtx.setTransform(1, 0, 0, 1, 0, 0);
    maskCtx.clearRect(0, 0, w, h);
    maskCtx.filter = `blur(${featherPx}px)`;
    maskCtx.fillStyle = '#fff';
    polyPath(maskCtx, lm, group, w, h);
    maskCtx.fill();
    maskCtx.restore();
    // 3. Apply mask to buffer (destination-in keeps only where mask is opaque)
    bufCtx.save();
    bufCtx.globalCompositeOperation = 'destination-in';
    bufCtx.drawImage(maskCanvas, 0, 0);
    bufCtx.restore();
    // 4. Composite onto main canvas with desired alpha
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.drawImage(bufCanvas, 0, 0);
    ctx.restore();
  }

  // ── Utility: load a script once ─────────────────────────────────────
  function loadScript(url) {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${url}"]`);
      if (existing) {
        if (existing.dataset.loaded) return resolve();
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error('Failed to load ' + url)));
        return;
      }
      const s = document.createElement('script');
      s.src = url;
      s.crossOrigin = 'anonymous';
      s.onload = () => { s.dataset.loaded = '1'; resolve(); };
      s.onerror = () => reject(new Error('Failed to load ' + url));
      document.head.appendChild(s);
    });
  }

  // ── Boot MediaPipe FaceMesh ─────────────────────────────────────────
  async function ensureFaceMesh() {
    if (faceMesh) return faceMesh;
    await loadScript(MEDIAPIPE_FACEMESH_URL);
    if (!window.FaceMesh) throw new Error('MediaPipe FaceMesh did not load');
    faceMesh = new window.FaceMesh({
      locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`
    });
    faceMesh.setOptions({
      maxNumFaces: 1,
      refineLandmarks: true,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5
    });
    faceMesh.onResults(onFaceMeshResults);
    return faceMesh;
  }

  // ── Profile loading ─────────────────────────────────────────────────
  function loadProfile(productId) {
    const el = document.getElementById(`ar-profile-${productId}`);
    if (!el) return null;
    try {
      const data = JSON.parse(el.textContent);
      // Manual override takes precedence
      if (data.manualOverride && typeof data.manualOverride === 'object') {
        return Object.assign({}, data.effects, data.manualOverride);
      }
      return data.effects;
    } catch (e) {
      console.warn('[JEJU AR] failed to parse profile', e);
      return null;
    }
  }

  function summarizeProfile(profile) {
    if (!profile) return 'Universal glow & hydration';
    const labels = {
      brighten: 'Brightening',
      smooth: 'Smoothing fine lines',
      hydrate: 'Deep hydration',
      redness_reduce: 'Calming redness',
      clarify: 'Clarifying blemishes',
      firm: 'Firming & lifting',
      sun_glow: 'Sun-protected glow',
      undereye_lift: 'Under-eye lift',
      lip_tint: 'Lip nourishment',
      even_tone: 'Evening skin tone',
      warm_glow: 'Glass-skin radiance',
      rejuvenate: 'Younger, regenerated skin',
      spot_fade: 'Fading dark spots & blemishes'
    };
    const sorted = Object.entries(profile)
      .filter(([, v]) => v > 0.05)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([k]) => labels[k] || k);
    if (!sorted.length) return 'Subtle radiance enhancement';
    return sorted.join(' · ');
  }

  // ── Camera ──────────────────────────────────────────────────────────
  // IMPORTANT: getUserMedia must be invoked SYNCHRONOUSLY from the click
  // handler on iOS Safari, otherwise the user-gesture context is lost
  // and the permission prompt silently fails. We therefore accept an
  // already-started stream promise from the click handler.
  async function startCamera(streamPromise) {
    setLoading('Requesting camera access…');
    hideError();
    try {
      stream = await streamPromise;
    } catch (err) {
      showError(err && err.name === 'NotAllowedError'
        ? 'Camera permission was denied. Please allow camera access in your browser settings, then tap Retry.'
        : 'We couldn\'t open the camera. Please make sure no other app is using it, then tap Retry.');
      throw err;
    }
    videoEl.srcObject = stream;
    try { await videoEl.play(); } catch (e) { /* autoplay block harmless */ }
    sizeCanvas();
    setLoading('Loading facial AI… (one-time, ~3MB)');
    try {
      await ensureFaceMesh();
    } catch (e) {
      showError('Could not load the face-detection model. Please check your connection and tap Retry.');
      throw e;
    }
    setLoading(null);
    pump();
  }

  // Fallback for browsers that don't yet support starting via promise
  function requestCameraStream() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      return Promise.reject(new Error('getUserMedia not supported'));
    }
    return navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
      audio: false
    });
  }

  function stopCamera() {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = null;
    if (stream) {
      stream.getTracks().forEach(t => t.stop());
      stream = null;
    }
    if (videoEl) videoEl.srcObject = null;
    lastLandmarks = null;
    if (ctx && canvasEl) ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
  }

  function sizeCanvas() {
    const w = videoEl.videoWidth || 1280;
    const h = videoEl.videoHeight || 720;
    canvasEl.width = w;
    canvasEl.height = h;
  }

  async function pump() {
    if (!videoEl || videoEl.readyState < 2) {
      rafId = requestAnimationFrame(pump);
      return;
    }
    try {
      await faceMesh.send({ image: videoEl });
    } catch (e) {
      console.warn('[JEJU AR] facemesh send error', e);
    }
    rafId = requestAnimationFrame(pump);
  }

  // ── Render ──────────────────────────────────────────────────────────
  function onFaceMeshResults(results) {
    if (!ctx || !canvasEl) return;
    const w = canvasEl.width;
    const h = canvasEl.height;

    // Draw raw video
    ctx.save();
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(results.image || videoEl, 0, 0, w, h);

    if (!showEffects || !results.multiFaceLandmarks || !results.multiFaceLandmarks.length || !currentProfile) {
      ctx.restore();
      return;
    }

    lastLandmarks = results.multiFaceLandmarks[0];
    applyEffects(ctx, lastLandmarks, w, h, currentProfile, intensity);
    ctx.restore();
  }

  // Convert normalized landmarks to pixel coords
  function pt(lm, i, w, h) {
    const p = lm[i];
    return [p.x * w, p.y * h];
  }
  function polyPath(ctx, lm, indices, w, h) {
    ctx.beginPath();
    indices.forEach((idx, i) => {
      const [x, y] = pt(lm, idx, w, h);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.closePath();
  }

  // ── New beauty pipeline (mirrors the AI scan after-image, divided 4 weeks) ──
  // Build a feathered face-mask canvas: face oval MINUS eyes/brows/mouth/nostrils.
  // Returned canvas is white inside the skin region (with feathered edge), transparent elsewhere.
  function buildBeautyMask(lm, w, h) {
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const mctx = c.getContext('2d');
    // Feather radius scales with frame size
    const feather = Math.max(6, Math.min(w, h) * 0.018);
    mctx.filter = `blur(${feather}px)`;
    // Paint face oval white
    mctx.fillStyle = '#fff';
    polyPath(mctx, lm, LM.FACE_OVAL, w, h);
    mctx.fill();
    // Subtract eyes & brows with a HEAVIER blur so the cutout eats the soft
    // mask edge that otherwise sits right on the lash line / lid (visible as
    // a white halo on the eye in every effect pass).
    mctx.globalCompositeOperation = 'destination-out';
    const eyeBlur = Math.max(10, Math.min(w, h) * 0.035);
    mctx.filter = `blur(${eyeBlur}px)`;
    [LM.LEFT_EYE, LM.RIGHT_EYE, LM.LEFT_BROW, LM.RIGHT_BROW].forEach(group => {
      polyPath(mctx, lm, group, w, h);
      mctx.fill();
    });
    // Lips & nostrils can stay on the lighter feather
    mctx.filter = `blur(${feather}px)`;
    [LM.LIPS_OUTER, LM.NOSTRILS].forEach(group => {
      polyPath(mctx, lm, group, w, h);
      mctx.fill();
    });
    return c;
  }

  // Frequency-separation-lite skin smoothing.
  // Blurs the current frame, masks to skin, then composites back with
  // globalAlpha = 1-keepDetail so original detail is partially preserved.
  function freqSepSmoothAR(ctx, maskCanvas, w, h, strength) {
    if (strength <= 0.01) return;
    const blurPx = Math.max(3, Math.min(w, h) * 0.012) * (0.6 + strength * 0.6);
    // Snapshot current frame
    const snap = document.createElement('canvas');
    snap.width = w; snap.height = h;
    const sctx = snap.getContext('2d');
    sctx.drawImage(ctx.canvas, 0, 0);
    // Blur the snapshot
    const blurred = document.createElement('canvas');
    blurred.width = w; blurred.height = h;
    const bctx = blurred.getContext('2d');
    bctx.filter = `blur(${blurPx.toFixed(2)}px)`;
    bctx.drawImage(snap, 0, 0);
    // Mask the blur to the skin region
    bctx.globalCompositeOperation = 'destination-in';
    bctx.filter = 'none';
    bctx.drawImage(maskCanvas, 0, 0);
    // Composite the masked-blur on top of the original with partial alpha
    const keepDetail = Math.max(0.78, 1 - strength * 0.22);
    ctx.save();
    ctx.globalAlpha = 1 - keepDetail;
    ctx.drawImage(blurred, 0, 0);
    ctx.restore();
  }

  // Under-eye lift: vertical squash within feathered under-eye polygon + soft brighten.
  function tightenUnderEyeAR(ctx, lm, w, h, idxs, u, eyeIdxs) {
    if (u <= 0.01) return;
    // Bounding box of the under-eye polygon
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const i of idxs) {
      const [x, y] = pt(lm, i, w, h);
      if (x < x0) x0 = x; if (y < y0) y0 = y;
      if (x > x1) x1 = x; if (y > y1) y1 = y;
    }
    const pad = 6;
    x0 = Math.max(0, Math.floor(x0 - pad));
    y0 = Math.max(0, Math.floor(y0 - pad));
    x1 = Math.min(w, Math.ceil(x1 + pad));
    y1 = Math.min(h, Math.ceil(y1 + pad));
    const bw = x1 - x0, bh = y1 - y0;
    if (bw < 4 || bh < 4) return;
    // Snapshot strip
    const strip = document.createElement('canvas');
    strip.width = bw; strip.height = bh;
    strip.getContext('2d').drawImage(ctx.canvas, x0, y0, bw, bh, 0, 0, bw, bh);
    // Feathered clip mask — and SUBTRACT the eye polygon (with extra padding/blur)
    // so the squashed strip can never bleed onto the eyeball or eyelashes.
    const m = document.createElement('canvas');
    m.width = w; m.height = h;
    const mctx = m.getContext('2d');
    mctx.filter = `blur(${Math.max(2, bh * 0.06)}px)`;
    mctx.fillStyle = '#fff';
    polyPath(mctx, lm, idxs, w, h);
    mctx.fill();
    if (eyeIdxs && eyeIdxs.length) {
      // Heavier blur on the eye cut-out so it eats the soft mask edge above the lower lid
      mctx.globalCompositeOperation = 'destination-out';
      mctx.filter = `blur(${Math.max(3, bh * 0.12)}px)`;
      polyPath(mctx, lm, eyeIdxs, w, h);
      mctx.fill();
    }
    // Squash much gentler than before — large shifts created a visible duplicated
    // skin strip on the lower lid each frame.
    const squash = 1 - Math.min(0.03, u * 0.04);
    const newH = bh * squash;
    const yOff = (bh - newH) * 0.5;
    // Use offscreen compositing: draw strip into a temp canvas masked, then blit
    const out = document.createElement('canvas');
    out.width = w; out.height = h;
    const octx = out.getContext('2d');
    octx.drawImage(strip, x0, y0 + yOff, bw, newH);
    octx.globalCompositeOperation = 'destination-in';
    octx.drawImage(m, 0, 0);
    ctx.save();
    ctx.drawImage(out, 0, 0);
    // Soft warm lighten fade, also clipped to the feathered mask so it never
    // bleeds onto the eye itself
    const fade = document.createElement('canvas');
    fade.width = w; fade.height = h;
    const fctx = fade.getContext('2d');
    fctx.fillStyle = `rgba(245, 220, 200, ${Math.min(0.40, u * 0.50).toFixed(3)})`;
    polyPath(fctx, lm, idxs, w, h);
    fctx.fill();
    fctx.globalCompositeOperation = 'destination-in';
    fctx.drawImage(m, 0, 0);
    ctx.globalCompositeOperation = 'lighten';
    ctx.drawImage(fade, 0, 0);
    ctx.restore();
  }

  function applyEffects(ctx, lm, w, h, profile, k) {
    // Day 0: no processing at all so user sees their raw camera feed for comparison
    if (k <= 0.001) return;
    const E = profile;
    // Map the four week intensities to clean quarter-stages of the AI scan
    // baseline (k=0.45 in jeju-ai-skincare.js). Anything in-between snaps to
    // the nearest stage so weeks 1→4 read as 25 / 50 / 75 / 100 %.
    let stage;
    if (k <= 0.015)      stage = 0.25;
    else if (k <= 0.03)  stage = 0.50;
    else if (k <= 0.05)  stage = 0.75;
    else                 stage = 1.00;
    const kEff = 0.45 * stage;
    const I = (v) => Math.min(1, (v || 0) * kEff);

    // Build feathered face mask (skin region only, eyes/brows/mouth/nostrils removed)
    const maskCanvas = buildBeautyMask(lm, w, h);

    // -- 1. Spot fade: blurred lighten pass over the skin mask --------
    //      Boosted alpha so blemishes visibly soften by Week 2.
    const spot = I(E.spot_fade) + I(E.redness_reduce) * 0.5;
    if (spot > 0.02) {
      const s = Math.min(1, spot);
      const blurPx = 10 + s * 16;
      const snap = document.createElement('canvas');
      snap.width = w; snap.height = h;
      const sctx = snap.getContext('2d');
      sctx.filter = `blur(${blurPx}px)`;
      sctx.drawImage(ctx.canvas, 0, 0);
      sctx.globalCompositeOperation = 'destination-in';
      sctx.filter = 'none';
      sctx.drawImage(maskCanvas, 0, 0);
      ctx.save();
      ctx.globalCompositeOperation = 'lighten';
      ctx.globalAlpha = Math.min(0.60, s * 0.75);
      ctx.drawImage(snap, 0, 0);
      ctx.restore();
    }

    // -- 2. Frequency-separation skin smoothing -----------------------
    //      Stronger smoothing per week so pores visibly soften.
    const smoothStr = Math.min(0.80,
      I(E.smooth) * 1.4 + I(E.clarify) * 0.4 + I(E.hydrate) * 0.2);
    freqSepSmoothAR(ctx, maskCanvas, w, h, smoothStr);

    // -- 3. Brighten + warm glow (soft-light) through skin mask -------
    const bright = I(E.brighten);
    const warm   = I(E.warm_glow);
    const evenT  = I(E.even_tone);
    if (bright > 0.005 || warm > 0.005 || evenT > 0.005) {
      const tint = document.createElement('canvas');
      tint.width = w; tint.height = h;
      const tctx = tint.getContext('2d');
      if (bright > 0.005) {
        tctx.fillStyle = `rgba(255, 248, 235, ${Math.min(0.30, bright * 0.45).toFixed(3)})`;
        tctx.fillRect(0, 0, w, h);
      }
      if (warm > 0.005) {
        tctx.fillStyle = `rgba(232, 196, 148, ${Math.min(0.18, warm * 0.28).toFixed(3)})`;
        tctx.fillRect(0, 0, w, h);
      }
      if (evenT > 0.005) {
        tctx.fillStyle = `rgba(232, 200, 168, ${Math.min(0.18, evenT * 0.25).toFixed(3)})`;
        tctx.fillRect(0, 0, w, h);
      }
      tctx.globalCompositeOperation = 'destination-in';
      tctx.drawImage(maskCanvas, 0, 0);
      ctx.save();
      ctx.globalCompositeOperation = 'soft-light';
      ctx.globalAlpha = 0.85;
      ctx.drawImage(tint, 0, 0);
      ctx.restore();
    }

    // -- 4. Subtle redness reduction (multiply pale-cyan over skin) ---
    const redReduce = I(E.redness_reduce) + I(E.clarify) * 0.4;
    if (redReduce > 0.02) {
      const tint = document.createElement('canvas');
      tint.width = w; tint.height = h;
      const tctx = tint.getContext('2d');
      tctx.fillStyle = `rgba(225, 240, 232, ${Math.min(0.30, redReduce * 0.38).toFixed(3)})`;
      tctx.fillRect(0, 0, w, h);
      tctx.globalCompositeOperation = 'destination-in';
      tctx.drawImage(maskCanvas, 0, 0);
      ctx.save();
      ctx.globalCompositeOperation = 'multiply';
      ctx.drawImage(tint, 0, 0);
      ctx.restore();
    }

    // (Under-eye lift removed entirely — the per-frame strip-squash and the
    //  warm fade both caused a visible white line on the lower lid, so the
    //  effect has been disabled per user request.)

    // -- 6. Lip tint (kept from old pipeline, gentle) -----------------
    if (I(E.lip_tint) > 0.02) {
      ctx.save();
      polyPath(ctx, lm, LM.LIPS_OUTER, w, h);
      ctx.clip();
      ctx.globalCompositeOperation = 'multiply';
      const a = Math.min(0.45, I(E.lip_tint) * 0.55);
      ctx.fillStyle = `rgba(210, 110, 110, ${a})`;
      ctx.fillRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'screen';
      ctx.fillStyle = `rgba(255, 230, 230, ${Math.min(0.20, I(E.lip_tint) * 0.25)})`;
      ctx.fillRect(0, 0, w, h);
      ctx.restore();
    }
  }

  // (Legacy helpers below are retained but no longer called by the main pipeline.)
  function applyEffects_LEGACY_UNUSED(ctx, lm, w, h, profile, k) {
    if (k <= 0.001) return;
    const E = profile;
    const kEff = Math.min(0.72, k * 12);
    const I = (v) => Math.min(1, (v || 0) * kEff);

    // -- 0. Spot / blemish fade (real local-average correction) -------
    //     Heavy-blur the face → that gives the local skin tone with no spots.
    //     Composite back via 'lighten' to fill dark spots, then via 'normal'
    //     blend at moderate alpha to fade red blemishes toward surrounding
    //     skin. Feathered face-oval mask so no seam shows.
    if (I(E.spot_fade) > 0.02) {
      drawSpotFade(ctx, lm, w, h, I(E.spot_fade));
    }

    // -- 1. Skin smoothing + blemish softening + pore minimisation ----
    //     Real bilateral-style smoothing: blur the skin, keep eyes/lips sharp.
    //     Kept SUBTLE — Week 4 ≈ a single soft pass at low alpha so it never
    //     looks like a white powder mask.
    if (I(E.smooth) > 0.005 || I(E.clarify) > 0.005 || I(E.hydrate) > 0.005) {
      const sIntensity = Math.min(1, I(E.smooth) + I(E.clarify) * 0.5 + I(E.hydrate) * 0.3);
      // 1 pass for weeks 1-3, 2 passes for week 4 maximum
      const passes = sIntensity > 0.5 ? 2 : 1;
      // Modest blur radius: Week 1 ≈ 3px, Week 4 ≈ 7px
      const baseBlur = 2 + sIntensity * 7;
      // Feather the mask edge so the chin/forehead/cheekbone seam dissolves
      // into surrounding pixels (no visible "filter line").
      const feather = 18 + sIntensity * 14;
      const passAlpha = Math.min(0.45, sIntensity * 0.55);

      for (let p = 0; p < passes; p++) {
        drawBlurredRegion(ctx, lm, LM.FACE_OVAL, w, h, baseBlur + p * 1.5, passAlpha, feather);
      }

      // Re-sharpen the eyes & lips back so the face never looks plastic
      if (videoEl && videoEl.readyState >= 2) {
        [LM.LEFT_UNDEREYE, LM.RIGHT_UNDEREYE, LM.LIPS_OUTER].forEach(group => {
          ctx.save();
          polyPath(ctx, lm, group, w, h);
          ctx.clip();
          ctx.filter = 'none';
          ctx.globalAlpha = 0.85;
          try { ctx.drawImage(videoEl, 0, 0, w, h); } catch (e) { /* ignore */ }
          ctx.restore();
        });
      }
    }

    // -- 2. Brightness / saturation lift over face oval ---------------
    const bright = I(E.brighten);
    const evenT  = I(E.even_tone);
    const warm   = I(E.warm_glow);
    if (bright > 0.01 || evenT > 0.01 || warm > 0.01) {
      ctx.save();
      polyPath(ctx, lm, LM.FACE_OVAL, w, h);
      ctx.clip();
      // Brightening: gentle ivory glow (max ~25% alpha at Week 4, not white-out)
      if (bright > 0.01) {
        ctx.globalCompositeOperation = 'soft-light';
        ctx.fillStyle = `rgba(255, 245, 225, ${Math.min(0.28, bright * 0.40)})`;
        ctx.fillRect(0, 0, w, h);
      }
      // Even tone: subtle beige
      if (evenT > 0.01) {
        ctx.globalCompositeOperation = 'overlay';
        ctx.fillStyle = `rgba(232, 200, 168, ${Math.min(0.20, evenT * 0.28)})`;
        ctx.fillRect(0, 0, w, h);
      }
      // Warm glow: faint golden warmth
      if (warm > 0.01) {
        ctx.globalCompositeOperation = 'soft-light';
        ctx.fillStyle = `rgba(212, 184, 118, ${Math.min(0.25, warm * 0.32)})`;
        ctx.fillRect(0, 0, w, h);
      }
      ctx.restore();
    }

    // -- 3. Redness reduction (subtract red channel via difference) ---
    const redReduce = I(E.redness_reduce) + I(E.clarify) * 0.5;
    if (redReduce > 0.02) {
      ctx.save();
      polyPath(ctx, lm, LM.FACE_OVAL, w, h);
      ctx.clip();
      ctx.globalCompositeOperation = 'multiply';
      ctx.fillStyle = `rgba(225, 240, 232, ${Math.min(0.30, redReduce * 0.35)})`;
      ctx.fillRect(0, 0, w, h);
      ctx.restore();
    }

    // -- 4. Hydrate / dewy highlights on cheeks & nose bridge ---------
    const dew = Math.max(I(E.hydrate), I(E.sun_glow));
    if (dew > 0.02) {
      ctx.save();
      ctx.globalCompositeOperation = 'screen';
      const a = Math.min(0.22, dew * 0.28);
      drawRadialHighlight(ctx, lm, LM.LEFT_CHEEK,  w, h, `rgba(255, 250, 240, ${a})`);
      drawRadialHighlight(ctx, lm, LM.RIGHT_CHEEK, w, h, `rgba(255, 250, 240, ${a})`);
      drawRadialHighlight(ctx, lm, LM.NOSE_BRIDGE, w, h, `rgba(255, 252, 245, ${a * 0.7})`, 0.5);
      ctx.restore();
    }

    // -- 5. Sun glow: cool dewy mist over whole face ------------------
    if (I(E.sun_glow) > 0.02) {
      ctx.save();
      polyPath(ctx, lm, LM.FACE_OVAL, w, h);
      ctx.clip();
      ctx.globalCompositeOperation = 'screen';
      ctx.fillStyle = `rgba(220, 240, 250, ${Math.min(0.10, I(E.sun_glow) * 0.13)})`;
      ctx.fillRect(0, 0, w, h);
      ctx.restore();
    }

    // -- 6. Under-eye lift: erase dark circles & puffy bags --------------
    //     Three sub-passes so eye-cream products visibly clear bags by Week 4:
    //      (a) blur the bag area itself (softens texture / shadow stippling)
    //      (b) 'lighten' blend pushes dark pixels up toward skin tone
    //      (c) soft-light warm glow finishes the brightened look
    if (I(E.undereye_lift) > 0.02) {
      const u = I(E.undereye_lift);
      // (a) Blur each bag area with a feathered mask so the seam doesn't show
      const bagBlur = 3 + u * 6;
      const bagAlpha = Math.min(0.60, u * 0.75);
      drawBlurredRegion(ctx, lm, LM.LEFT_UNDEREYE_LOWER,  w, h, bagBlur, bagAlpha, 10);
      drawBlurredRegion(ctx, lm, LM.RIGHT_UNDEREYE_LOWER, w, h, bagBlur, bagAlpha, 10);
      // (b) Lighten dark pixels up to skin tone (this is the actual bag-erase)
      [LM.LEFT_UNDEREYE_LOWER, LM.RIGHT_UNDEREYE_LOWER].forEach(group => {
        ctx.save();
        polyPath(ctx, lm, group, w, h);
        ctx.clip();
        ctx.globalCompositeOperation = 'lighten';
        ctx.filter = `blur(${(4 + u * 4).toFixed(1)}px)`;
        ctx.fillStyle = `rgba(232, 198, 175, ${Math.min(0.55, u * 0.60)})`;
        ctx.fillRect(0, 0, w, h);
        ctx.restore();
      });
      // (c) Warm soft-light glow on top for that 'awake' look
      const a = Math.min(0.55, u * 0.65);
      drawUnderEyeBrighten(ctx, lm, LM.LEFT_UNDEREYE_LOWER, w, h, a);
      drawUnderEyeBrighten(ctx, lm, LM.RIGHT_UNDEREYE_LOWER, w, h, a);
    }

    // -- 7. Firm: subtle cheekbone + jaw highlight (mock contour) -----
    if (I(E.firm) > 0.02) {
      ctx.save();
      ctx.globalCompositeOperation = 'overlay';
      const a = Math.min(0.20, I(E.firm) * 0.25);
      drawRadialHighlight(ctx, lm, [234, 93, 132], w, h, `rgba(255, 240, 220, ${a})`, 0.6);
      drawRadialHighlight(ctx, lm, [454, 323, 361], w, h, `rgba(255, 240, 220, ${a})`, 0.6);
      ctx.globalCompositeOperation = 'multiply';
      polyPath(ctx, lm, LM.JAW, w, h);
      ctx.strokeStyle = `rgba(140, 110, 90, ${a * 0.6})`;
      ctx.lineWidth = 8;
      ctx.filter = 'blur(6px)';
      ctx.stroke();
      ctx.restore();
    }

    // -- 8. Lip tint --------------------------------------------------
    if (I(E.lip_tint) > 0.02) {
      ctx.save();
      polyPath(ctx, lm, LM.LIPS_OUTER, w, h);
      ctx.clip();
      ctx.globalCompositeOperation = 'multiply';
      const a = Math.min(0.45, I(E.lip_tint) * 0.55);
      ctx.fillStyle = `rgba(210, 110, 110, ${a})`;
      ctx.fillRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'screen';
      ctx.fillStyle = `rgba(255, 230, 230, ${Math.min(0.20, I(E.lip_tint) * 0.25)})`;
      ctx.fillRect(0, 0, w, h);
      ctx.restore();
    }

    // -- 9. Rejuvenate: younger-looking skin --------------------------
    //     Triggered by Regenerate Cream, Age-Reversal Cream, Essential Set.
    //     Three sub-effects:
    //      (a) Extra targeted blur on FOREHEAD + UNDEREYE regions →
    //          softens horizontal forehead lines and crow's-feet.
    //      (b) Plump cheek highlight → suggests collagen restoration.
    //      (c) Soft inner jaw shadow → visually tightens the jawline.
    const rej = I(E.rejuvenate);
    if (rej > 0.02) {
      // (a) Wrinkle softening: re-blur forehead & undereye on top of base smoothing
      const wrinkleBlur = 3 + rej * 8;
      const wrinkleAlpha = Math.min(0.55, rej * 0.65);
      [LM.FOREHEAD, LM.LEFT_UNDEREYE, LM.RIGHT_UNDEREYE].forEach(group => {
        drawBlurredRegion(ctx, lm, group, w, h, wrinkleBlur, wrinkleAlpha, 14);
      });

      // (b) Plump cheek highlight — warm luminosity high on cheekbone
      ctx.save();
      ctx.globalCompositeOperation = 'screen';
      const plumpA = Math.min(0.30, rej * 0.38);
      drawRadialHighlight(ctx, lm, LM.LEFT_CHEEK,  w, h, `rgba(255, 235, 215, ${plumpA})`, 0.85);
      drawRadialHighlight(ctx, lm, LM.RIGHT_CHEEK, w, h, `rgba(255, 235, 215, ${plumpA})`, 0.85);
      ctx.restore();

      // (c) Jaw-tightening: soft dark stroke along inner jaw line (multiply)
      ctx.save();
      polyPath(ctx, lm, LM.FACE_OVAL, w, h);
      ctx.clip();
      polyPath(ctx, lm, LM.JAW, w, h);
      ctx.globalCompositeOperation = 'multiply';
      ctx.strokeStyle = `rgba(120, 90, 75, ${Math.min(0.32, rej * 0.38)})`;
      ctx.lineWidth = 10;
      ctx.filter = `blur(${(5 + rej * 4).toFixed(1)}px)`;
      ctx.stroke();
      ctx.restore();
    }
  }

  function drawRadialHighlight(ctx, lm, indices, w, h, color, sizeScale) {
    const sz = sizeScale || 1.0;
    let cx = 0, cy = 0;
    for (const i of indices) { const [x, y] = pt(lm, i, w, h); cx += x; cy += y; }
    cx /= indices.length; cy /= indices.length;
    const radius = Math.max(w, h) * 0.08 * sz;
    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
    grad.addColorStop(0, color);
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(cx - radius, cy - radius, radius * 2, radius * 2);
  }

  function drawUnderEyeBrighten(ctx, lm, indices, w, h, alpha) {
    ctx.save();
    polyPath(ctx, lm, indices, w, h);
    ctx.clip();
    ctx.globalCompositeOperation = 'soft-light';
    ctx.filter = 'blur(8px)';
    ctx.fillStyle = `rgba(255, 245, 230, ${alpha})`;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();
  }

  /**
   * Real spot / blemish fade.
   *  - Heavy-blurs the current main canvas (= local skin-tone average,
   *    spots & blemishes smeared into surrounding skin).
   *  - Masks that blurred copy to a feathered face-oval shape.
   *  - Composites back twice:
   *      (a) 'lighten' at high alpha  → raises dark spots / PIH to skin tone
   *      (b) 'source-over' at mid alpha → fades red blemishes by averaging
   *           toward the blurred (de-reddened) tone.
   */
  function drawSpotFade(ctx, lm, w, h, strength) {
    ensureBuffers(w, h);
    const blurPx = 12 + strength * 18;       // 12-30 px depending on week
    const featherPx = 14 + strength * 10;

    // 1) Blur main canvas into bufCanvas
    bufCtx.save();
    bufCtx.setTransform(1, 0, 0, 1, 0, 0);
    bufCtx.clearRect(0, 0, w, h);
    bufCtx.filter = `blur(${blurPx.toFixed(1)}px)`;
    bufCtx.drawImage(canvasEl, 0, 0, w, h);
    bufCtx.restore();

    // 2) Feathered face-oval alpha mask into maskCanvas
    maskCtx.save();
    maskCtx.setTransform(1, 0, 0, 1, 0, 0);
    maskCtx.clearRect(0, 0, w, h);
    maskCtx.filter = `blur(${featherPx.toFixed(1)}px)`;
    maskCtx.fillStyle = '#fff';
    polyPath(maskCtx, lm, LM.FACE_OVAL, w, h);
    maskCtx.fill();
    maskCtx.restore();

    // 3) Apply feathered mask to blurred copy
    bufCtx.save();
    bufCtx.globalCompositeOperation = 'destination-in';
    bufCtx.drawImage(maskCanvas, 0, 0);
    bufCtx.restore();

    // 4a) Lighten pass → erase dark spots (dark pixels replaced by lighter avg)
    ctx.save();
    ctx.globalCompositeOperation = 'lighten';
    ctx.globalAlpha = Math.min(0.78, strength * 0.90);
    ctx.drawImage(bufCanvas, 0, 0);
    ctx.restore();

    // 4b) Normal blend → fade red blemishes / general tone evening
    ctx.save();
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = Math.min(0.45, strength * 0.55);
    ctx.drawImage(bufCanvas, 0, 0);
    ctx.restore();
  }

  // ── UI helpers ──────────────────────────────────────────────────────
  function setLoading(text) {
    if (!loadingEl) return;
    if (!text) { loadingEl.hidden = true; return; }
    loadingEl.hidden = false;
    if (loadingTextEl) loadingTextEl.textContent = text;
  }
  function showError(msg) {
    if (loadingEl) loadingEl.hidden = true;
    if (!errorEl) return;
    errorEl.hidden = false;
    if (errorTextEl) errorTextEl.textContent = msg;
  }
  function hideError() {
    if (errorEl) errorEl.hidden = true;
  }

  // ── Capture photo ───────────────────────────────────────────────────
  function capturePhoto() {
    if (!canvasEl) return;
    // Mirror for natural selfie orientation
    const out = document.createElement('canvas');
    out.width = canvasEl.width;
    out.height = canvasEl.height;
    const octx = out.getContext('2d');
    octx.translate(out.width, 0);
    octx.scale(-1, 1);
    octx.drawImage(canvasEl, 0, 0);
    out.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `jeju-tryon-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }, 'image/png');
  }

  // ── Add to cart shortcut ────────────────────────────────────────────
  async function addToCart() {
    if (!currentProductHandle) return;
    try {
      // Fetch the product to get the first available variant
      const res = await fetch(`/products/${currentProductHandle}.js`);
      const data = await res.json();
      const firstAvailable = (data.variants || []).find(v => v.available) || data.variants[0];
      if (!firstAvailable) return;
      const cartRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: firstAvailable.id, quantity: 1 }] })
      });
      if (cartRes.ok) {
        // Trigger Dawn cart drawer refresh if present
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh'));
        window.location.href = '/cart';
      }
    } catch (e) {
      console.warn('[JEJU AR] add to cart failed', e);
    }
  }

  // Buy Now: add current product to cart and jump straight to checkout
  async function buyNow() {
    if (!currentProductHandle) return;
    try {
      const res = await fetch(`/products/${currentProductHandle}.js`);
      const data = await res.json();
      const firstAvailable = (data.variants || []).find(v => v.available) || data.variants[0];
      if (!firstAvailable) return;
      const cartRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: firstAvailable.id, quantity: 1 }] })
      });
      if (cartRes.ok) window.location.href = '/checkout';
    } catch (e) {
      console.warn('[JEJU AR] buy now failed', e);
    }
  }

  // ── Modal lifecycle ─────────────────────────────────────────────────
  async function openModal(button, streamPromise) {
    const productId = button.dataset.productId;
    currentProductHandle = button.dataset.productHandle ||
      (document.querySelector('[data-ar-add-to-cart]')?.dataset.productHandle);

    modalEl = document.getElementById('jeju-ar-modal');
    if (!modalEl) return;

    bindRefs();
    buildPicker();

    setCurrentProduct(productId);

    hideError();
    // Default to Day 0 baseline (no effect) so user sees their actual skin first
    setWeek(0);
    showEffects = true;
    toggleEl.setAttribute('aria-pressed', 'true');
    if (toggleLabelEl) toggleLabelEl.textContent = 'After';

    if (typeof modalEl.showModal === 'function') {
      try { modalEl.showModal(); } catch (e) { modalEl.setAttribute('open', ''); }
    } else {
      modalEl.setAttribute('open', '');
    }

    // True full-screen for immersive AR (hides mobile URL bar). Best-effort.
    requestFullscreen(modalEl);

    try {
      await startCamera(streamPromise || requestCameraStream());
    } catch (e) { /* error already shown */ }
  }

  function requestFullscreen(el) {
    try {
      const fn = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
      if (fn) fn.call(el).catch(() => {});
    } catch (e) { /* ignore */ }
  }
  function exitFullscreen() {
    try {
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        const fn = document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen || document.msExitFullscreen;
        if (fn) fn.call(document).catch(() => {});
      }
    } catch (e) { /* ignore */ }
  }

  // Set the week (0-4) and update intensity, button state, status pill
  function setWeek(week) {
    const btn = modalEl && modalEl.querySelector(`[data-ar-week="${week}"]`);
    const ints = btn ? parseFloat(btn.dataset.intensity) : 0;
    intensity = isNaN(ints) ? 0 : ints;
    modalEl && modalEl.querySelectorAll('[data-ar-week]').forEach(b => {
      const active = b.getAttribute('data-ar-week') === String(week);
      b.classList.toggle('is-active', active);
      b.setAttribute('aria-pressed', String(active));
    });
    // Update status pill so user gets immediate feedback that the change registered
    const statusEl = modalEl && modalEl.querySelector('[data-ar-week-status]');
    if (statusEl) {
      const labels = {
        0: 'Day 0 · your skin now',
        1: 'Week 1 · first hint of glow',
        2: 'Week 2 · tone evening out',
        3: 'Week 3 · pores refined',
        4: 'Week 4 · 30-day porcelain finish'
      };
      statusEl.textContent = labels[week] || ('Week ' + week);
      // Brief highlight flash so the change is unmistakable
      statusEl.classList.remove('is-flash');
      void statusEl.offsetWidth;
      statusEl.classList.add('is-flash');
    }
    // Mirror on the floating badge above the face so the user sees it without looking away
    const badge = modalEl && modalEl.querySelector('[data-ar-stage-badge]');
    const badgeLabel = modalEl && modalEl.querySelector('[data-ar-stage-badge-label]');
    if (badgeLabel) {
      const blabels = {
        0: 'Day 0 · your skin now',
        1: 'Week 1 · first hint of glow',
        2: 'Week 2 · tone evening out',
        3: 'Week 3 · pores refined',
        4: 'Week 4 · 30-day porcelain finish'
      };
      badgeLabel.textContent = blabels[week] || ('Week ' + week);
    }
    if (badge) {
      badge.setAttribute('data-week', String(week));
      badge.classList.remove('is-flash');
      void badge.offsetWidth;
      badge.classList.add('is-flash');
    }
  }

  // Load profile for a given product id and update UI
  function setCurrentProduct(productId) {
    currentProfile = loadProfile(productId);
    if (summaryEl) summaryEl.textContent = summarizeProfile(currentProfile);
    // Find the product in the catalogue and update title + cart handle
    const catEntry = (window.__jejuArCatalogue || []).find(p => String(p.id) === String(productId));
    if (catEntry) {
      currentProductHandle = catEntry.handle;
      const titleEl = modalEl && modalEl.querySelector('[data-ar-product-title]');
      if (titleEl) titleEl.textContent = catEntry.title;
      const cartBtn = modalEl && modalEl.querySelector('[data-ar-add-to-cart]');
      if (cartBtn) cartBtn.dataset.productHandle = catEntry.handle;
      // Mark active tile
      modalEl && modalEl.querySelectorAll('.jeju-ar-picker__item').forEach(el => {
        el.classList.toggle('is-active', el.dataset.productId === String(productId));
      });
    }
  }

  // Build the product strip from the catalogue script tag (rendered by Liquid)
  function buildPicker() {
    const picker = modalEl && modalEl.querySelector('[data-ar-picker]');
    if (!picker || picker.dataset.built === '1') return;
    const cataEl = document.getElementById('ar-product-catalogue');
    if (!cataEl) return;
    let list = [];
    try { list = JSON.parse(cataEl.textContent); } catch (e) { return; }
    window.__jejuArCatalogue = list;
    // Sort so Essentials Set comes first (after Full Routine)
    list.sort((a, b) => (b.isEssentials ? 1 : 0) - (a.isEssentials ? 1 : 0));
    list.forEach(p => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'jeju-ar-picker__item' + (p.isEssentials ? ' jeju-ar-picker__item--essentials' : '');
      btn.dataset.productId = p.id;
      btn.dataset.productHandle = p.handle;
      btn.setAttribute('aria-label', 'Try ' + p.title);
      btn.innerHTML =
        (p.image ? `<img class="jeju-ar-picker__img" src="${p.image}" alt="" loading="lazy">` : '<span class="jeju-ar-picker__img jeju-ar-picker__img--ph"></span>') +
        (p.isEssentials ? '<span class="jeju-ar-picker__badge">Set</span>' : '') +
        `<span class="jeju-ar-picker__title">${escapeHtml(p.title)}</span>`;
      picker.appendChild(btn);
    });
    picker.dataset.built = '1';
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({
      '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;'
    }[c]));
  }

  // Synthetic "Full Routine" profile – every effect maxed for a wow demo
  const FULL_ROUTINE_PROFILE = {
    brighten: 1.0, smooth: 1.0, hydrate: 1.0, redness_reduce: 0.85,
    clarify: 0.7, firm: 0.8, sun_glow: 0.7, undereye_lift: 0.9,
    lip_tint: 0.7, even_tone: 0.9, warm_glow: 0.6
  };

  function closeModal() {
    stopCamera();
    exitFullscreen();
    if (!modalEl) return;
    if (typeof modalEl.close === 'function' && modalEl.open) modalEl.close();
    else modalEl.removeAttribute('open');
  }

  function bindRefs() {
    if (!modalEl) return;
    videoEl       = modalEl.querySelector('[data-ar-video]');
    canvasEl      = modalEl.querySelector('[data-ar-canvas]');
    ctx           = canvasEl.getContext('2d');
    intensityEl   = modalEl.querySelector('[data-ar-intensity]'); // may be null (slider removed)
    intensityLabelEl = modalEl.querySelector('[data-ar-intensity-value]');
    toggleEl      = modalEl.querySelector('[data-ar-toggle]');
    toggleLabelEl = modalEl.querySelector('[data-ar-toggle-label]');
    summaryEl     = modalEl.querySelector('[data-ar-effect-summary]');
    loadingEl     = modalEl.querySelector('[data-ar-loading]');
    loadingTextEl = modalEl.querySelector('[data-ar-loading-text]');
    errorEl       = modalEl.querySelector('[data-ar-error]');
    errorTextEl   = modalEl.querySelector('[data-ar-error-text]');
  }

  // ── Event delegation ────────────────────────────────────────────────
  document.addEventListener('click', (ev) => {
    const launch = ev.target.closest('[data-jeju-ar-launch]');
    if (launch) {
      ev.preventDefault();
      // CRITICAL: start the camera request synchronously inside the click
      // handler so iOS Safari preserves the user-gesture context.
      const streamPromise = requestCameraStream();
      // Swallow rejections here – openModal/startCamera will surface them.
      streamPromise.catch(() => {});
      openModal(launch, streamPromise);
      return;
    }
    if (ev.target.closest('[data-jeju-ar-close]')) {
      closeModal();
      return;
    }
    if (ev.target.closest('[data-ar-retry]')) {
      // Re-request the camera with a fresh user gesture
      const streamPromise = requestCameraStream();
      streamPromise.catch(() => {});
      hideError();
      stopCamera();
      startCamera(streamPromise).catch(() => {});
      return;
    }
    if (ev.target.closest('[data-ar-routine]')) {
      currentProfile = Object.assign({}, FULL_ROUTINE_PROFILE);
      if (summaryEl) summaryEl.textContent = 'Full routine · every Jeju effect combined';
      modalEl && modalEl.querySelectorAll('.jeju-ar-picker__item').forEach(el => {
        el.classList.toggle('is-active', el.hasAttribute('data-ar-routine'));
      });
      return;
    }
    const pick = ev.target.closest('.jeju-ar-picker__item[data-product-id]');
    if (pick) {
      setCurrentProduct(pick.dataset.productId);
      return;
    }
    if (ev.target.closest('[data-ar-toggle]')) {
      showEffects = !showEffects;
      ev.target.closest('[data-ar-toggle]').setAttribute('aria-pressed', String(showEffects));
      if (toggleLabelEl) toggleLabelEl.textContent = showEffects ? 'After' : 'Before';
      return;
    }
    if (ev.target.closest('[data-ar-capture]')) {
      capturePhoto();
      return;
    }
    if (ev.target.closest('[data-ar-add-to-cart]')) {
      addToCart();
      return;
    }
    // Floating Buy Now – add current product to cart and go straight to checkout
    if (ev.target.closest('[data-ar-buy-now]')) {
      buyNow();
      return;
    }
    // Week selector buttons
    const weekBtn = ev.target.closest('[data-ar-week]');
    if (weekBtn) {
      setWeek(parseInt(weekBtn.getAttribute('data-ar-week'), 10));
      return;
    }
  });

  document.addEventListener('input', (ev) => {
    if (ev.target.matches('[data-ar-intensity]')) {
      // Legacy slider still works if rendered
      intensity = parseInt(ev.target.value, 10) / 100;
      if (intensityLabelEl) intensityLabelEl.textContent = ev.target.value + '%';
    }
  });

  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape' && modalEl && modalEl.open) closeModal();
  });

  // Close on backdrop click (when using <dialog>)
  document.addEventListener('click', (ev) => {
    if (ev.target === modalEl) closeModal();
  });

  // Auto-highlight on product page when arriving with ?ar=1 or #ar (from card "Try on" badge)
  // NOTE: iOS Safari requires a real user tap for camera access, so we scroll + pulse the button
  // instead of auto-clicking it.
  function maybeAutoLaunch() {
    try {
      const url = new URL(window.location.href);
      const wantsAr = url.searchParams.get('ar') === '1' || window.location.hash === '#ar';
      if (!wantsAr) return;
      const launch = document.querySelector('[data-jeju-ar-launch]');
      if (!launch) return;
      setTimeout(() => {
        launch.scrollIntoView({ behavior: 'smooth', block: 'center' });
        launch.classList.add('jeju-ar-launch--pulse');
        setTimeout(() => launch.classList.remove('jeju-ar-launch--pulse'), 4500);
      }, 250);
    } catch (e) { /* ignore */ }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', maybeAutoLaunch);
  } else {
    maybeAutoLaunch();
  }

})();
